import { faTableTennis } from '@fortawesome/free-solid-svg-icons';
import classnames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';

import PageSecondHeader from '@/components/PageSecondHeader';
import SquareButton from '@/ui/atoms/SquareButton';
import HalfLayout from '@/ui/layouts/HalfLayout';
import PageHeader from '@/ui/molecules/PageHeader';
import SectionHeader from '@/ui/molecules/SectionHeader';
import {
  clickVisitLandingPageLink,
  clickVisitPostPageLink,
} from '@/utils/analytics/log-event';
import { Course, Movie } from '@/utils/lesson-course';

type Props = {
  movie: Movie;
  index: number;
  course: Course;
  children?: React.ReactNode;
};

type SectionProps = {
  children?: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ children }) => {
  return (
    <div className="rounded-md bg-orange-100 p-5">
      <div className="mb-5 text-center">
        プレミアムサービスで4,700本以上の卓球レッスン動画が見放題！
      </div>
      {children}
    </div>
  );
};

const Component: React.FC<Props> = ({ movie, index, course }) => {
  return (
    <HalfLayout>
      <PageHeader caption={movie.title} />
      <div className="relative aspect-video h-full w-full overflow-hidden bg-gray-300">
        <iframe
          allowFullScreen
          frameBorder="0"
          src={`https://player.vimeo.com/video/${movie.vimeoId}`}
          className="absolute inset-0 h-full w-full"
        />
      </div>
      <div className="my-2 rounded border border-gray-400 p-3">
        <SectionHeader caption="レッスンコース概要" />
        <div className="mb-3 flex flex-row gap-4">
          <div className="relative block aspect-video h-full w-[140px] rounded border border-gray-200 bg-gray-100">
            <Image
              fill
              alt={course.title}
              src={course.imageUrl}
              className="rounded object-cover"
            />
          </div>
          <div className="flex-1 text-gray-600">
            <div className="mb-2 font-bold">{course.title}</div>
            <div className="text-xs">{course.description}</div>
          </div>
        </div>
        <SectionHeader caption="担当講師" />
        <div className="flex flex-row gap-4">
          <div className="flex w-[140px] items-center justify-center">
            <div className="relative h-[80px] w-[80px] overflow-hidden rounded-full border border-gray-200 bg-gray-100">
              <Image src={course.coach.coachImageUrl} alt="" fill />
            </div>
          </div>
          <div className="flex-1 text-gray-600">
            <div className="mb-2 font-bold">{course.coach.coachName}</div>
            <div className="text-xs">{course.coach.description}</div>
          </div>
        </div>
      </div>

      <Section>
        <SquareButton
          url="/lp/base"
          caption="プレミアム会員登録はこちら"
          icon={faTableTennis}
          logEvent={() =>
            clickVisitLandingPageLink('lesson_courses_under_player')
          }
        />
      </Section>

      <div>
        <PageSecondHeader
          caption={`${course.title}  全${course.movies.length}レッスン`}
        />
        {course.movies.map((movie, movieIndex) => (
          <Link
            key={movie.vimeoId}
            href={`/courses/${course.id}/${movieIndex + 1}`}
            onClick={() => clickVisitPostPageLink('lesson_courses_list')}
            className={classnames(
              'flex flex-row border-b border-gray-300 py-3 last:border-none active:bg-gray-200',
              index === movieIndex
                ? 'cursor-default bg-gray-200'
                : 'hover:bg-gray-100 active:bg-gray-200',
            )}
          >
            <div className="relative mr-3 block aspect-video w-[190px] border border-gray-200 bg-gray-100">
              <Image
                fill
                alt={movie.title}
                src={movie.imageUrl}
                className="rounded object-cover"
              />
              <div className="absolute left-2 top-2 rounded bg-black/70 p-0.5 text-xs font-bold text-white">{`#${
                movieIndex + 1
              }`}</div>

              <div className="absolute bottom-1 right-2 rounded bg-black/70 p-0.5 text-xs font-bold text-white">
                {movie.length}
              </div>
            </div>

            <div className="flex-1 text-gray-600">
              <div className="mb-2 font-bold">{movie.title}</div>
              <div className="text-sm">{movie.coachName}</div>
            </div>
          </Link>
        ))}
      </div>

      <Section>
        <SquareButton
          url="/lp/base"
          icon={faTableTennis}
          caption="プレミアム会員登録はこちら"
          logEvent={() =>
            clickVisitLandingPageLink('lesson_courses_under_content')
          }
        />
      </Section>
    </HalfLayout>
  );
};

export default Component;
